import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../../../../components/Layout';
import BopBackLink from '../../../../components/BopBackLink';
import ApiInspector from '../../../../components/ApiInspector';
import { apiFetch } from '../../../../lib/api';
import { showToast } from '../../../../components/Toast';
import { formatCurrency } from '../../../../utils/formatters';
import { useFiltroSocieta } from '../../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../../context/PermessiContext';
import { useDirtyForm } from '../../../../hooks/useDirtyForm';
import { API_DOCS } from './WizardFattureDifferite.api';
import './WizardFattureDifferite.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type TipoDoc = 'DDT' | 'SAL';
type TipoFattura = 'DIFFERITA' | 'IMMEDIATA';

interface DocFatturabile {
  tipoDoc: TipoDoc;
  id: number;
  numero: string;
  data: string | null;
  ordine: { id: number; numero: string } | null;
  commessa: { id: number; numero: string; descrizione: string | null } | null;
  nRighe: number;
  imponibileStimato: number | string | null;
  storni: number | string;
}

interface GruppoFatturabile {
  chiave: string;
  sedeId: number | null;
  sedeNome: string | null;
  nominativoId: number | null;
  clienteRagioneSociale: string | null;
  documenti: DocFatturabile[];
}

// Fattura in composizione (step 2): documenti sempre della stessa (sede, cliente)
interface FatturaProposta {
  uid: number;
  gruppoChiave: string;
  tipoFattura: TipoFattura;
  documenti: DocFatturabile[];
}

interface RisultatoGenerazione {
  etichetta: string;
  esito: 'CREATA' | 'SALTATO' | 'ERRORE';
  fatturaId?: number;
  tipoFattura?: TipoFattura;
  clienteRagioneSociale?: string | null;
  totale?: number | string;
  nRighe?: number;
  motivo?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Chiave univoca di un documento (gli id DDT e SAL vivono in tabelle diverse)
function docKey(d: Pick<DocFatturabile, 'tipoDoc' | 'id'>): string {
  return `${d.tipoDoc}-${d.id}`;
}

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

function BadgeTipoDoc({ tipoDoc }: { tipoDoc: TipoDoc }) {
  return tipoDoc === 'DDT'
    ? <span className="bop-badge bop-badge-info">DDT vendita</span>
    : <span className="bop-badge bop-badge-warning">SAL</span>;
}

function BadgeTipoFattura({ tipo }: { tipo: TipoFattura | undefined }) {
  if (!tipo) return <span className="bop-text-muted">—</span>;
  return tipo === 'DIFFERITA'
    ? <span className="bop-badge bop-badge-info">Differita</span>
    : <span className="bop-badge bop-badge-neutral">Immediata</span>;
}

function renderEsito(esito: RisultatoGenerazione['esito']): React.ReactNode {
  if (esito === 'CREATA') return <span className="bop-badge bop-badge-success">Creata</span>;
  if (esito === 'SALTATO') return <span className="bop-badge bop-badge-warning">Saltato</span>;
  return <span className="bop-badge bop-badge-danger">Errore</span>;
}

// Totale stimato di una fattura proposta (somma degli imponibili stimati)
function totaleCard(f: FatturaProposta): number {
  const tot = f.documenti.reduce(
    (s, d) => s + (d.imponibileStimato !== null ? Number(d.imponibileStimato) : 0),
    0
  );
  return Math.round(tot * 100) / 100;
}

// Importo stimato + eventuale nota storni acconto (SAL)
function ImportoStimato({ doc }: { doc: DocFatturabile }) {
  return (
    <div className="wfd-importo">
      {doc.imponibileStimato !== null
        ? <span className="wfd-importo-valore">{formatCurrency(Number(doc.imponibileStimato))}</span>
        : <span className="bop-text-muted">—</span>}
      {doc.tipoDoc === 'SAL' && Number(doc.storni) > 0 && (
        <span className="wfd-importo-storni bop-text-muted">
          − storni acconto {formatCurrency(Number(doc.storni))} già dedotti
        </span>
      )}
    </div>
  );
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function WizardFattureDifferite() {
  const navigate = useNavigate();
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { isSuperadmin } = usePermesso();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Step del wizard
  const [step, setStep] = useState<1 | 2>(1);

  // Ricerca testo client-side (step 1)
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [gruppi, setGruppi] = useState<GruppoFatturabile[]>([]);
  const [loading, setLoading] = useState(false);

  // Selezione documenti per gruppo (chiave gruppo → set di docKey)
  const [sel, setSel] = useState<Map<string, Set<string>>>(new Map());
  // Accordion step 1: chiavi dei gruppi chiusi (default tutti aperti)
  const [chiusi, setChiusi] = useState<Set<string>>(new Set());

  // Composizione (step 2) + firma della selezione con cui è stata generata
  const [fatture, setFatture] = useState<FatturaProposta[]>([]);
  const [firmaComposizione, setFirmaComposizione] = useState('');
  const uidRef = useRef(1);
  const generaUid = () => uidRef.current++;

  const [creando, setCreando] = useState(false);

  // Esiti (null = wizard attivo, valorizzato = pannello risultati)
  const [risultati, setRisultati] = useState<RisultatoGenerazione[] | null>(null);
  const [createCount, setCreateCount] = useState(0);

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // ── Caricamento documenti fatturabili ───────────────────────────────────────

  const caricaGruppi = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      const qs = params.toString() ? `?${params.toString()}` : '';

      // @api GET /api/fatture/fatturabili?societaId -> {"success":true,"data":[{"chiave":"string","sedeId":"number|null","sedeNome":"string|null","nominativoId":"number|null","clienteRagioneSociale":"string|null","documenti":[{"tipoDoc":"DDT|SAL","id":"number","numero":"string","data":"string|null","ordine":{"id":"number","numero":"string"},"commessa":{"id":"number","numero":"string","descrizione":"string"},"nRighe":"number","imponibileStimato":"number|null","storni":"number"}]}]}
      const r = await apiFetch<{ success: boolean; data: GruppoFatturabile[] }>(
        `/api/fatture/fatturabili${qs}`
      );
      const data = r.data || [];
      setGruppi(data);
      // Di default tutti i documenti selezionati
      setSel(new Map(data.map(g => [g.chiave, new Set(g.documenti.map(docKey))])));
      setChiusi(new Set());
      // Reset composizione: verrà rigenerata al passaggio allo step 2
      setStep(1);
      setFatture([]);
      setFirmaComposizione('');
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento dei documenti fatturabili'), 'error');
      setGruppi([]);
      setSel(new Map());
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff]);

  useEffect(() => { caricaGruppi(); }, [caricaGruppi]);

  // ── Filtro testo client-side (numero doc / ordine / commessa / cliente) ─────

  const gruppiFiltrati = useMemo(() => {
    if (!q) return gruppi;
    return gruppi.filter(g => {
      if ((g.clienteRagioneSociale || '').toLowerCase().includes(q)) return true;
      return g.documenti.some(d =>
        [
          d.numero,
          d.ordine?.numero || '',
          d.commessa?.numero || '',
          d.commessa?.descrizione || '',
        ].join(' ').toLowerCase().includes(q)
      );
    });
  }, [gruppi, q]);

  const gruppiByChiave = useMemo(
    () => new Map(gruppi.map(g => [g.chiave, g])),
    [gruppi]
  );

  // ── Selezione (step 1) ──────────────────────────────────────────────────────

  const toggleDoc = (chiave: string, key: string) => {
    setSel(prev => {
      const next = new Map(prev);
      const s = new Set(next.get(chiave) || []);
      if (s.has(key)) s.delete(key); else s.add(key);
      next.set(chiave, s);
      return next;
    });
  };

  const toggleGruppo = (g: GruppoFatturabile) => {
    setSel(prev => {
      const next = new Map(prev);
      const s = next.get(g.chiave) || new Set<string>();
      if (s.size === g.documenti.length) {
        next.set(g.chiave, new Set());
      } else {
        next.set(g.chiave, new Set(g.documenti.map(docKey)));
      }
      return next;
    });
  };

  const toggleAccordion = (chiave: string) => {
    setChiusi(prev => {
      const next = new Set(prev);
      if (next.has(chiave)) next.delete(chiave); else next.add(chiave);
      return next;
    });
  };

  const nDocSelezionati = useMemo(
    () => Array.from(sel.values()).reduce((tot, s) => tot + s.size, 0),
    [sel]
  );

  // Firma canonica della selezione: se cambia, la composizione va rigenerata
  const firmaSelezione = useMemo(
    () => gruppi
      .map(g => {
        const s = sel.get(g.chiave);
        if (!s || s.size === 0) return null;
        return `${g.chiave}:${Array.from(s).sort().join(',')}`;
      })
      .filter(Boolean)
      .join(';'),
    [gruppi, sel]
  );

  // Dirty quando ci sono documenti selezionati e il wizard è in corso
  const isDirty = risultati === null && nDocSelezionati > 0;
  useDirtyForm(isDirty, 'Fatturazione — composizione fatture');

  // ── Composizione (step 2) ───────────────────────────────────────────────────

  // Stato iniziale: una fattura riepilogativa per ogni gruppo (sede, cliente)
  const buildComposizione = (): FatturaProposta[] => {
    const proposte: FatturaProposta[] = [];
    for (const g of gruppi) {
      const s = sel.get(g.chiave);
      if (!s || s.size === 0) continue;
      const docs = g.documenti.filter(d => s.has(docKey(d)));
      if (docs.length === 0) continue;
      proposte.push({
        uid: generaUid(),
        gruppoChiave: g.chiave,
        tipoFattura: 'DIFFERITA',
        documenti: docs,
      });
    }
    return proposte;
  };

  const avantiComposizione = () => {
    if (nDocSelezionati === 0) return;
    // Rigenera solo se la selezione è cambiata rispetto alla composizione corrente
    if (firmaSelezione !== firmaComposizione) {
      setFatture(buildComposizione());
      setFirmaComposizione(firmaSelezione);
    }
    setStep(2);
  };

  // Ordinamento stabile: le card seguono l'ordine dei gruppi, poi di creazione
  const fattureOrdinate = useMemo(() => {
    const ordinate: FatturaProposta[] = [];
    for (const g of gruppi) {
      for (const f of fatture) {
        if (f.gruppoChiave === g.chiave) ordinate.push(f);
      }
    }
    return ordinate;
  }, [gruppi, fatture]);

  const numeroByUid = useMemo(() => {
    const m = new Map<number, number>();
    fattureOrdinate.forEach((f, i) => m.set(f.uid, i + 1));
    return m;
  }, [fattureOrdinate]);

  const setTipoFatturaCard = (uid: number, tipo: TipoFattura) => {
    setFatture(prev => prev.map(f => (f.uid === uid ? { ...f, tipoFattura: tipo } : f)));
  };

  // Esplode la card in N card da un documento ciascuna
  const dividiFattura = (uid: number) => {
    const idx = fatture.findIndex(f => f.uid === uid);
    if (idx < 0) return;
    const f = fatture[idx];
    if (f.documenti.length <= 1) return;
    const esplose: FatturaProposta[] = f.documenti.map(d => ({
      uid: generaUid(),
      gruppoChiave: f.gruppoChiave,
      tipoFattura: f.tipoFattura,
      documenti: [d],
    }));
    setFatture([...fatture.slice(0, idx), ...esplose, ...fatture.slice(idx + 1)]);
  };

  // Sposta un documento in un'altra fattura dello stesso gruppo (o in una nuova)
  const spostaDocumento = (fromUid: number, doc: DocFatturabile, target: string) => {
    const from = fatture.find(f => f.uid === fromUid);
    if (!from) return;
    let next = fatture.map(f =>
      f.uid === fromUid
        ? { ...f, documenti: f.documenti.filter(d => docKey(d) !== docKey(doc)) }
        : f
    );
    if (target === 'nuova') {
      next = [...next, {
        uid: generaUid(),
        gruppoChiave: from.gruppoChiave,
        tipoFattura: from.tipoFattura,
        documenti: [doc],
      }];
    } else {
      const targetUid = parseInt(target, 10);
      next = next.map(f =>
        f.uid === targetUid ? { ...f, documenti: [...f.documenti, doc] } : f
      );
    }
    // Una card svuotata sparisce
    setFatture(next.filter(f => f.documenti.length > 0));
  };

  const nDocComposizione = useMemo(
    () => fatture.reduce((tot, f) => tot + f.documenti.length, 0),
    [fatture]
  );

  // ── Generazione ─────────────────────────────────────────────────────────────

  const crea = async () => {
    if (fattureOrdinate.length === 0 || creando) return;
    const payload = fattureOrdinate
      .filter(f => f.documenti.length > 0)
      .map(f => ({
        tipoFattura: f.tipoFattura,
        ddtIds: f.documenti.filter(d => d.tipoDoc === 'DDT').map(d => d.id),
        salIds: f.documenti.filter(d => d.tipoDoc === 'SAL').map(d => d.id),
      }));

    setCreando(true);
    try {
      // @api POST /api/fatture/genera-composte {"fatture":[{"tipoFattura":"DIFFERITA|IMMEDIATA","ddtIds":["number"],"salIds":["number"]}]} -> {"success":true,"data":{"risultati":[{"etichetta":"string","esito":"CREATA|SALTATO|ERRORE","fatturaId":"number","tipoFattura":"DIFFERITA|IMMEDIATA","clienteRagioneSociale":"string","totale":"number","nRighe":"number","motivo":"string"}],"create":"number"}}
      const res = await apiFetch<{
        success: boolean;
        data?: { risultati: RisultatoGenerazione[]; create: number };
        error?: unknown;
      }>('/api/fatture/genera-composte', {
        method: 'POST',
        body: JSON.stringify({ fatture: payload }),
      });
      if (!res.success || !res.data) {
        showToast(estraiErrore({ payload: res }, 'Errore nella creazione delle fatture'), 'error');
        return;
      }
      const create = res.data.create || 0;
      showToast(`${create} fattur${create === 1 ? 'a creata' : 'e create'} in bozza`, 'success');
      setRisultati(res.data.risultati || []);
      setCreateCount(create);
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nella creazione delle fatture'), 'error');
    } finally {
      setCreando(false);
    }
  };

  // Reset completo e ricarica dei documenti residui
  const componiAltre = () => {
    setRisultati(null);
    setCreateCount(0);
    setStep(1);
    setFatture([]);
    setFirmaComposizione('');
    caricaGruppi();
  };

  // ── Render: sottotitolo dinamico ────────────────────────────────────────────

  const sottotitolo = risultati !== null
    ? 'Esito della generazione delle fatture in bozza'
    : step === 1
      ? 'Seleziona i documenti da fatturare: DDT di vendita emessi e SAL approvati'
      : 'Componi le fatture: accorpa o dividi i documenti selezionati prima di creare le bozze';

  return (
    <Layout>
      <div className="wfd">
        <BopBackLink to="/commerciale/attivo/fatture" label="Torna alle fatture" />

        {/* Header */}
        <div className="bop-page-header">
          <div className="wfd-header-titles">
            <h1 className="bop-page-title">Fatturazione</h1>
            <p className="wfd-nota bop-text-muted">{sottotitolo}</p>
          </div>
          {risultati === null && (
            <div className="wfd-steps">
              <span className={`wfd-step ${step === 1 ? 'wfd-step--active' : ''}`}>
                <span className="wfd-step__num">1</span> Documenti
              </span>
              <span className="wfd-step__sep" aria-hidden="true">→</span>
              <span className={`wfd-step ${step === 2 ? 'wfd-step--active' : ''}`}>
                <span className="wfd-step__num">2</span> Composizione
              </span>
            </div>
          )}
        </div>

        {risultati !== null ? (
          /* ── Pannello esiti ─────────────────────────────────────────────── */
          <div className="bop-card">
            <div className="bop-card__header">
              <h2 className="bop-card__title">
                Esito generazione — {createCount} fattur{createCount === 1 ? 'a creata' : 'e create'}
              </h2>
            </div>
            <div className="bop-card__body">
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Fattura</th>
                    <th>Cliente</th>
                    <th>Tipo</th>
                    <th>Esito</th>
                    <th>Bozza</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {risultati.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <div className="bop-empty-state">Nessun esito da mostrare</div>
                      </td>
                    </tr>
                  ) : (
                    risultati.map((r, i) => (
                      <tr key={`${r.fatturaId ?? 'x'}-${i}`}>
                        <td className="wfd-esito-etichetta">{r.etichetta}</td>
                        <td>{r.clienteRagioneSociale || <span className="bop-text-muted">—</span>}</td>
                        <td><BadgeTipoFattura tipo={r.tipoFattura} /></td>
                        <td>{renderEsito(r.esito)}</td>
                        <td>
                          {r.fatturaId ? (
                            <Link
                              className="wfd-fattura-link"
                              to={`/commerciale/attivo/fatture/${r.fatturaId}`}
                            >
                              Apri bozza — {formatCurrency(Number(r.totale ?? 0))}
                            </Link>
                          ) : (
                            <span className="bop-text-muted">—</span>
                          )}
                        </td>
                        <td>
                          {r.motivo
                            ? <span className="bop-text-muted">{r.motivo}</span>
                            : <span className="bop-text-muted">—</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="wfd-esiti-actions">
                <button className="bop-btn bop-btn-secondary" onClick={componiAltre}>
                  Componi altre
                </button>
                <button
                  className="bop-btn bop-btn-primary"
                  onClick={() => navigate('/commerciale/attivo/fatture')}
                >
                  Vai alle fatture
                </button>
              </div>
            </div>
          </div>
        ) : step === 1 ? (
          /* ── STEP 1 — Documenti ─────────────────────────────────────────── */
          <>
            {/* Filtri */}
            <div className="bop-filter-bar wfd-filters-row">
              {!isDisabled && (
                <div className="bop-form-group">
                  <label className="bop-label">Società</label>
                  <select
                    className="bop-select"
                    value={societaIdLocale}
                    onChange={e => setSocietaIdLocale(e.target.value)}
                  >
                    {isSuperadmin && <option value="">Tutte</option>}
                    {listaSocieta.map(s => (
                      <option key={s.id} value={String(s.id)}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="bop-form-group">
                <label className="bop-label">Cerca</label>
                <input
                  className="bop-input wfd-search-input"
                  value={qInput}
                  onChange={e => setQInput(e.target.value)}
                  placeholder="Numero documento, ordine, commessa, cliente..."
                />
              </div>
            </div>

            {/* Gruppi (accordion sede — cliente) */}
            {!pronta ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  Seleziona una società per visualizzare i documenti da fatturare
                </div>
              </div>
            ) : loading ? (
              <div className="bop-card">
                <div className="bop-empty-state">Caricamento…</div>
              </div>
            ) : gruppiFiltrati.length === 0 ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  <h3 className="bop-empty-state__title">Nessun documento da fatturare</h3>
                  <p className="bop-empty-state__text">
                    Non ci sono DDT di vendita emessi né SAL approvati in attesa di fatturazione.
                  </p>
                </div>
              </div>
            ) : (
              gruppiFiltrati.map(g => {
                const selGruppo = sel.get(g.chiave) || new Set<string>();
                const tuttiSel = g.documenti.length > 0 && selGruppo.size === g.documenti.length;
                const parziale = selGruppo.size > 0 && !tuttiSel;
                const aperto = !chiusi.has(g.chiave);
                return (
                  <div className="bop-card wfd-gruppo" key={g.chiave}>
                    <button
                      type="button"
                      className="wfd-gruppo__header"
                      onClick={() => toggleAccordion(g.chiave)}
                    >
                      <input
                        type="checkbox"
                        className="wfd-gruppo__check"
                        checked={tuttiSel}
                        ref={el => { if (el) el.indeterminate = parziale; }}
                        onChange={() => toggleGruppo(g)}
                        onClick={e => e.stopPropagation()}
                      />
                      <span className="wfd-gruppo__titolo">
                        {g.sedeNome || 'Sede non specificata'}
                        <span className="wfd-gruppo__separatore"> — </span>
                        {g.clienteRagioneSociale || 'Cliente non specificato'}
                      </span>
                      <span className="wfd-gruppo__conteggio bop-text-muted">
                        {selGruppo.size}/{g.documenti.length} documenti selezionati
                      </span>
                      <span className="wfd-gruppo__chevron" aria-hidden="true">
                        {aperto ? '▾' : '▸'}
                      </span>
                    </button>
                    {aperto && (
                      <div className="wfd-gruppo__body">
                        <table className="bop-table">
                          <thead>
                            <tr>
                              <th className="wfd-col-check"></th>
                              <th>Tipo</th>
                              <th>Numero</th>
                              <th>Data</th>
                              <th>Ordine</th>
                              <th>Commessa</th>
                              <th className="bop-text-right">Righe</th>
                              <th className="bop-text-right">Importo stimato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.documenti.map(d => {
                              const key = docKey(d);
                              return (
                                <tr key={key}>
                                  <td className="wfd-col-check">
                                    <input
                                      type="checkbox"
                                      checked={selGruppo.has(key)}
                                      onChange={() => toggleDoc(g.chiave, key)}
                                    />
                                  </td>
                                  <td><BadgeTipoDoc tipoDoc={d.tipoDoc} /></td>
                                  <td><span className="wfd-doc-numero">{d.numero}</span></td>
                                  <td>{formatDateIT(d.data)}</td>
                                  <td>
                                    {d.ordine
                                      ? d.ordine.numero
                                      : <span className="bop-text-muted">—</span>}
                                  </td>
                                  <td>
                                    {d.commessa ? (
                                      <span>
                                        {d.commessa.numero}
                                        {d.commessa.descrizione ? ` — ${d.commessa.descrizione}` : ''}
                                      </span>
                                    ) : (
                                      <span className="bop-text-muted">—</span>
                                    )}
                                  </td>
                                  <td className="bop-text-right">{d.nRighe}</td>
                                  <td className="bop-text-right"><ImportoStimato doc={d} /></td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Footer sticky step 1 */}
            <div className="bop-card wfd-footer">
              <div className="bop-card__body">
                <div className="wfd-footer-row">
                  <span className="wfd-riepilogo-testo">
                    <strong className="wfd-riepilogo-count">{nDocSelezionati}</strong>{' '}
                    document{nDocSelezionati === 1 ? 'o selezionato' : 'i selezionati'}
                  </span>
                  <div className="wfd-footer-actions">
                    <button
                      className="bop-btn bop-btn-primary"
                      onClick={avantiComposizione}
                      disabled={nDocSelezionati === 0}
                    >
                      Avanti: componi le fatture →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          /* ── STEP 2 — Composizione ──────────────────────────────────────── */
          <>
            {fattureOrdinate.length === 0 ? (
              <div className="bop-card">
                <div className="bop-empty-state">
                  <h3 className="bop-empty-state__title">Nessuna fattura da comporre</h3>
                  <p className="bop-empty-state__text">
                    Torna ai documenti e seleziona almeno un DDT o un SAL da fatturare.
                  </p>
                </div>
              </div>
            ) : (
              fattureOrdinate.map(f => {
                const gruppo = gruppiByChiave.get(f.gruppoChiave);
                const numero = numeroByUid.get(f.uid);
                const altre = fattureOrdinate.filter(
                  x => x.gruppoChiave === f.gruppoChiave && x.uid !== f.uid
                );
                const puoNuova = f.documenti.length > 1;
                return (
                  <div className="bop-card wfd-fattura-card" key={f.uid}>
                    <div className="wfd-fattura-card__header">
                      <div className="wfd-fattura-card__titolo-blocco">
                        <span className="wfd-fattura-card__titolo">
                          Fattura {numero} — {gruppo?.clienteRagioneSociale || 'Cliente non specificato'}{' '}
                          ({gruppo?.sedeNome || 'Sede non specificata'})
                        </span>
                        <span className="wfd-fattura-card__totale">
                          Totale stimato:{' '}
                          <span className="wfd-fattura-card__totale-importo">
                            {formatCurrency(totaleCard(f))}
                          </span>
                          <span className="bop-text-muted">
                            {' '}— {f.documenti.length} document{f.documenti.length === 1 ? 'o' : 'i'}
                          </span>
                        </span>
                      </div>
                      <div className="wfd-fattura-card__controlli">
                        <label className="wfd-fattura-card__tipo">
                          <span className="wfd-fattura-card__tipo-label">Tipo serie</span>
                          <select
                            className="bop-select wfd-fattura-card__tipo-select"
                            value={f.tipoFattura}
                            onChange={e => setTipoFatturaCard(f.uid, e.target.value as TipoFattura)}
                          >
                            <option value="DIFFERITA">Differita (riepilogativa)</option>
                            <option value="IMMEDIATA">Immediata</option>
                          </select>
                        </label>
                        {f.documenti.length > 1 && (
                          <button
                            className="bop-btn bop-btn-secondary wfd-fattura-card__dividi"
                            onClick={() => dividiFattura(f.uid)}
                          >
                            Dividi: una fattura per documento
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="wfd-fattura-card__body">
                      <table className="bop-table">
                        <thead>
                          <tr>
                            <th>Tipo</th>
                            <th>Numero</th>
                            <th>Data</th>
                            <th className="bop-text-right">Importo stimato</th>
                            <th className="wfd-col-sposta">Sposta in…</th>
                          </tr>
                        </thead>
                        <tbody>
                          {f.documenti.map(d => (
                            <tr key={docKey(d)}>
                              <td><BadgeTipoDoc tipoDoc={d.tipoDoc} /></td>
                              <td><span className="wfd-doc-numero">{d.numero}</span></td>
                              <td>{formatDateIT(d.data)}</td>
                              <td className="bop-text-right"><ImportoStimato doc={d} /></td>
                              <td className="wfd-col-sposta">
                                {(altre.length > 0 || puoNuova) ? (
                                  <select
                                    className="bop-select wfd-sposta-select"
                                    value=""
                                    onChange={e => {
                                      if (e.target.value) spostaDocumento(f.uid, d, e.target.value);
                                    }}
                                  >
                                    <option value="">Sposta in…</option>
                                    {altre.map(x => (
                                      <option key={x.uid} value={String(x.uid)}>
                                        Fattura {numeroByUid.get(x.uid)}
                                      </option>
                                    ))}
                                    {puoNuova && <option value="nuova">Nuova fattura</option>}
                                  </select>
                                ) : (
                                  <span className="bop-text-muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            )}

            {/* Footer sticky step 2 */}
            <div className="bop-card wfd-footer">
              <div className="bop-card__body">
                <div className="wfd-footer-row">
                  <span className="wfd-riepilogo-testo">
                    {fattureOrdinate.length === 1 ? (
                      <>Verrà creata <strong className="wfd-riepilogo-count">1</strong> fattura</>
                    ) : (
                      <>
                        Verranno create{' '}
                        <strong className="wfd-riepilogo-count">{fattureOrdinate.length}</strong> fatture
                      </>
                    )}
                    {nDocComposizione > 0 && (
                      <span className="bop-text-muted">
                        {' '}— {nDocComposizione} document{nDocComposizione === 1 ? 'o' : 'i'}
                      </span>
                    )}
                  </span>
                  <div className="wfd-footer-actions">
                    <button
                      className="bop-btn bop-btn-secondary"
                      onClick={() => setStep(1)}
                      disabled={creando}
                    >
                      ← Torna ai documenti
                    </button>
                    <button
                      className="bop-btn bop-btn-primary"
                      onClick={crea}
                      disabled={creando || fattureOrdinate.length === 0}
                    >
                      {creando
                        ? 'Creazione…'
                        : `Crea ${fattureOrdinate.length} fattur${fattureOrdinate.length === 1 ? 'a' : 'e'} in bozza`}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
